import { HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, switchMap } from "rxjs";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { IGetGoalParams, IGoal, IGoalInTree } from "@gtmhub/goals/models";
import { RequestConfig } from "@webapp/core/abstracts/models/request-config.model";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { GTMHUB_ADDITIONAL_PARAMS } from "@webapp/core/http/interceptors/track-data.interceptor";
import { GtmhubAdditionalParams } from "@webapp/core/http/models/http.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { IBulkDeleteResponse } from "@webapp/okrs/models/okrs-response.models";
import { Goal, GoalDTO } from "../models/goal.models";
import { GoalsApiService } from "./goals-api.service";
import { GoalsState } from "./goals-state.service";

@Injectable({
  providedIn: "any",
})
export class GoalsFacade extends BaseFacade<Goal, GoalDTO, GoalsState, GoalsApiService> {
  constructor(state: GoalsState, api: GoalsApiService, private progressColoring: ProgressColoring) {
    super(state, api);
  }

  public deleteGoals$(goalsIds: string[]): Observable<IBulkDeleteResponse> {
    return this.deleteMany$(goalsIds, {
      ...new RequestConfig(),
      url: this.apiService.getBulkDeleteGoalsEndpoint(),
    });
  }

  public getGoal$(goalId: string, params?: IGetGoalParams, additionalGtmhubParams?: GtmhubAdditionalParams): Observable<IGoal> {
    const context = { context: new HttpContext().set(GTMHUB_ADDITIONAL_PARAMS, additionalGtmhubParams) };
    return this.get$<IGoal>(goalId, {
      ...new RequestConfig(),
      ...context,
      queryParams: { ...params },
    }).pipe(switchMap((goal) => this.applyColoringToGoal(goal)));
  }

  private applyColoringToGoal(goal: IGoalInTree): Observable<IGoal> {
    goal.color = this.progressColoring.getColor(goal.attainment);
    goal.aggregatedAttainmentColor = this.progressColoring.getColor(goal.fullAggregatedAttainment);

    if (goal.metrics) {
      for (const metric of goal.metrics) {
        this.applyColoringToMetric(metric);
      }
    }

    return of(goal);
  }

  private applyColoringToMetric(metric: Metric): Metric {
    metric.color = this.progressColoring.getColor(metric.attainment);
    return metric;
  }
}
